import { z } from "zod";

const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;

// A service URL must have at least one alphanumeric label, a dot, and a 2+ letter
// TLD (e.g. example.com). The scheme is optional, but when present it must be http
// or https. Port, path, query and fragment are allowed.
const serviceUrlRegex =
  /^(https?:\/\/)?([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?::\d+)?([/?#]\S*)?$/i;

const serviceUrl = z
  .string()
  .refine((value) => {
    const trimmed = value.trim();
    return trimmed === "" || serviceUrlRegex.test(trimmed);
  }, "Enter a valid URL, e.g. example.com");

// Validation guarantees any scheme already present is http or https, so a
// missing scheme is the only case to handle.
export function normalizeServiceUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export const serviceCreateSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be 255 characters or less"),
  description: z
    .string()
    .max(1000, "Description must be 1000 characters or less")
    .optional()
    .or(z.literal("")),
  url: serviceUrl,
  text_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  background_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  accent_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  logo: z.instanceof(File, { message: "Logo is required" }),
  banner: z.instanceof(File).optional().nullable(),
});

// Logo is validated separately in the component when editing.
export const serviceEditSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be 255 characters or less"),
  description: z
    .string()
    .max(1000, "Description must be 1000 characters or less")
    .optional()
    .or(z.literal("")),
  url: serviceUrl,
  text_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  background_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  accent_color: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (#RRGGBB)")
    .optional()
    .or(z.literal("")),
  logo: z.instanceof(File).optional().nullable(),
  banner: z.instanceof(File).optional().nullable(),
});

// Backward compatibility alias
export const serviceFormSchema = serviceEditSchema;

export type ServiceCreateFormData = z.infer<typeof serviceCreateSchema>;
export type ServiceEditFormData = z.infer<typeof serviceEditSchema>;
export type ServiceFormData = z.infer<typeof serviceFormSchema>;
